import React, { useState, useRef } from 'react';
import emailjs from 'emailjs-com';
import { useLanguage } from '../../hooks/useLanguage';
import Section from '../ui/Section';
import Button from '../ui/Button';
import { Mail, Phone, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';

const Contact = () => {
    const { t } = useLanguage();
    const form = useRef();
    const [status, setStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');

        try {
            // EmailJS credentials provided by user
            const SERVICE_ID = 'service_ujb5r8j';
            const TEMPLATE_ID = 'template_aer604j';
            const PUBLIC_KEY = 'hz_HZLgJq0nNNRHCK';

            await emailjs.sendForm(
                SERVICE_ID,
                TEMPLATE_ID,
                form.current,
                PUBLIC_KEY
            );

            setStatus('success');
            setFormData({ firstName: '', lastName: '', email: '', message: '' });

            // Reset status after 5 seconds
            setTimeout(() => setStatus('idle'), 5000);
        } catch (error) {
            console.error('EmailJS Error:', error);
            setStatus('error');
            setTimeout(() => setStatus('idle'), 5000);
        }
    };

    return (
        <Section id="contact" background="slate">
            <div className="grid lg:grid-cols-2 gap-16">
                <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">{t.contact.getInTouch}</h2>
                    <p className="text-slate-600 text-lg mb-10">
                        {t.contact.description}
                    </p>

                    <div className="space-y-8">
                        <div className="flex items-start gap-4">
                            <div className="bg-white p-3 rounded-lg shadow-sm text-gold-600">
                                <MapPin className="w-6 h-6" />
                            </div>
                            <a
                                href="https://www.google.com/maps/search/?api=1&query=Mitrovačka+66,+36300+Novi+Pazar,+Srbija"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group"
                            >
                                <h3 className="font-bold text-slate-900 mb-1 group-hover:text-gold-600 transition-colors">{t.contact.visitUs}</h3>
                                <p className="text-slate-600 group-hover:text-slate-900 transition-colors">Mitrovačka 66, 36300<br />Novi Pazar, Srbija</p>
                            </a>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="bg-white p-3 rounded-lg shadow-sm text-gold-600">
                                <Mail className="w-6 h-6" />
                            </div>
                            <a href="mailto:info@nopa.rs" className="group">
                                <h3 className="font-bold text-slate-900 mb-1 group-hover:text-gold-600 transition-colors">{t.contact.emailUs}</h3>
                                <p className="text-slate-600 group-hover:text-slate-900 transition-colors">info@nopa.rs</p>
                            </a>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="bg-white p-3 rounded-lg shadow-sm text-gold-600">
                                <Phone className="w-6 h-6" />
                            </div>
                            <a href="tel:+381637376932" className="group">
                                <h3 className="font-bold text-slate-900 mb-1 group-hover:text-gold-600 transition-colors">{t.contact.callUs}</h3>
                                <p className="text-slate-600 group-hover:text-slate-900 transition-colors">+381 63 7376 932</p>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-100">
                    <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.firstName}</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    required
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent outline-none transition-all"
                                    placeholder={t.contact.placeholderGeneric}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.lastName}</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    required
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent outline-none transition-all"
                                    placeholder={t.contact.placeholderGeneric}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.email}</label>
                            <input
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent outline-none transition-all"
                                placeholder={t.contact.placeholderGeneric}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.message}</label>
                            <textarea
                                name="message"
                                required
                                rows="4"
                                value={formData.message}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent outline-none transition-all"
                                placeholder={t.contact.placeholderMessage}
                            ></textarea>
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={status === 'sending'}
                        >
                            {status === 'sending' ? t.contact.sending : t.contact.sendMessage}
                        </Button>

                        {status === 'success' && (
                            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
                                <CheckCircle2 className="w-5 h-5" />
                                <p>{t.contact.success}</p>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
                                <AlertCircle className="w-5 h-5" />
                                <p>{t.contact.error}</p>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </Section>
    );
};

export default Contact;

